import {type MatchParams} from '@wandb/weave/common/types/base';

/** The workspace the user is on: its project and any sweep or group. */
export type SingleRunNavParams = Pick<
  MatchParams,
  'entityName' | 'groupName' | 'projectName' | 'sweepName'
>;

/** The run to link to. */
export type SingleRunTarget = {
  entityName: string;
  projectName: string;
  runName: string;
};

/**
 * Builds the link to a run's workspace in single-run mode.
 *
 * - A run in the same project as the current sweep or group links inside that
 *   sweep or group; a sweep wins when both are set.
 * - A run from another project (e.g. a cross-project pinned run) links to its
 *   own project's run page without sweep or group context.
 * - `searchString` (e.g. `?nw=...`) is appended so the named workspace stays.
 */
export function buildSingleRunLink(
  navParams: SingleRunNavParams,
  run: SingleRunTarget,
  searchString: string
): string {
  const {entityName, projectName, runName} = run;
  const isSameProject =
    navParams.entityName === entityName &&
    navParams.projectName === projectName;

  let basePath = `/${entityName}/${projectName}/runs/${runName}`;
  if (isSameProject && navParams.sweepName) {
    basePath = `/${entityName}/${projectName}/sweeps/${navParams.sweepName}/runs/${runName}`;
  } else if (isSameProject && navParams.groupName) {
    basePath = `/${entityName}/${projectName}/groups/${navParams.groupName}/runs/${runName}`;
  }
  return basePath + searchString;
}
